import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Snackbar, Alert } from '@mui/material';
import type { ClientAuthErrorEvent } from '@/lib/api/interceptors/clientAuthErrorInterceptor';

/**
 * Component that listens for client authorization errors and shows user-friendly messages
 * Automatically redirects to /client-select when client access is denied
 */
export function ClientAuthErrorHandler() {
  const navigate = useNavigate();
  const [error, setError] = useState<{ message: string; code: string } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleClientAuthError = (event: Event) => {
      const customEvent = event as ClientAuthErrorEvent;
      const { errorCode, errorMessage } = customEvent.detail;

      // Map error codes to user-friendly messages
      let userMessage = errorMessage;
      switch (errorCode) {
        case 'INVALID_CLIENT_ACCESS':
          userMessage = 'You do not have access to this client. Please select a valid client.';
          break;
        case 'CLIENT_HEADER_REQUIRED': // Deprecated: kept for backward compatibility
        case 'ACTIVE_CLIENT_REQUIRED':
          userMessage = 'Please select a client to continue.';
          break;
        case 'INVALID_CLIENT_GUID':
          userMessage = 'Invalid client selection. Please select a client again.';
          break;
      }

      setError({ message: userMessage, code: errorCode });
      setOpen(true);

      // Redirect to client selection page after a brief delay
      setTimeout(() => {
        navigate({ to: '/client-select' });
      }, 1500);
    };

    window.addEventListener('client:auth-error', handleClientAuthError);

    return () => {
      window.removeEventListener('client:auth-error', handleClientAuthError);
    };
  }, [navigate]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: '100%' }}>
        {error?.message}
      </Alert>
    </Snackbar>
  );
}
